import { useLocation, useNavigate, Link } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import "./LegalApp.css";
import Navbar from "./Navbar";
import Footer from "./Footer";

/** Same TableSection pattern as GeneratedCS.js */
const TableSection = ({ children }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (containerRef.current) containerRef.current.scrollLeft = 0;
    }, 100);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="table-section" ref={containerRef}>
      {children}
    </div>
  );
};

const NumberedList = ({ items }) => {
  const arr = Array.isArray(items) ? items : [];
  if (arr.length === 0) return <div>-</div>;

  return (
    <div>
      {arr.map((x, i) => (
        <div key={i} style={{ marginBottom: "8px" }}>
          <strong>{i + 1}.</strong> {x}
        </div>
      ))}
    </div>
  );
};

/* ---------------- Single Table: Discrepancies (1x1) ---------------- */
const DiscrepanciesTable = ({ discrepancies = [] }) => {
  return (
    <TableSection>
      <div className="fir-table-container">
        <table className="fir-table">
          <thead>
            <tr>
              <th style={{ width: "35%" }}>Title</th>
              <th>Summary</th>
            </tr>
          </thead>

          <tbody>
            {discrepancies.length === 0 ? (
              <tr>
                <td colSpan={2} style={{ textAlign: "center", padding: "12px" }}>
                  No discrepancies found.
                </td>
              </tr>
            ) : (
              discrepancies.map((d, idx) => (
                <tr key={d?.id ?? `${d?.title ?? "disc"}-${idx}`}>
                  <td style={{ verticalAlign: "top", paddingTop: "15px" }}>
                    {d?.title || ""}
                  </td>
                  <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                    {d?.summary || ""}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </TableSection>
  );
};

const OffensesMadeOutTable = ({ offensesSummary = [] }) => {
  const renderTwoLineItems = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) return <span>-</span>;

    return (
      <ol style={{ margin: 0, paddingLeft: "18px" }}>
        {items.map((it, idx) => (
          <li key={idx} style={{ marginBottom: "10px" }}>
            <div>{it?.brief || "-"}</div>
            <div style={{ marginTop: "4px" }}>
              <strong>Proves:</strong> {it?.proves || "-"}
            </div>
          </li>
        ))}
      </ol>
    );
  };

  return (
    <TableSection>
      <div className="fir-table-container">
        <table className="fir-table">
          <thead>
            <tr>
              <th style={{ width: "18%" }}>Accused Name</th>
              <th style={{ width: "24%" }}>Offenses</th>
              <th style={{ width: "29%" }}>Evidence</th>
              <th style={{ width: "29%" }}>Witnesses</th>
            </tr>
          </thead>

          <tbody>
            {(!offensesSummary || offensesSummary.length === 0) ? (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", padding: "12px" }}>
                  No offenses summary available.
                </td>
              </tr>
            ) : (
              offensesSummary.map((row, rowIdx) => {
                const supported = Array.isArray(row?.supported_offenses)
                  ? row.supported_offenses
                  : [];

                return (
                  <tr key={`${row?.accused_name || "accused"}-${rowIdx}`}>
                    {/* Column 1 */}
                    <td style={{ verticalAlign: "top", paddingTop: "15px" }}>
                      {row?.accused_name || "-"}
                    </td>

                    {/* Column 2 */}
                    <td style={{ verticalAlign: "top", paddingTop: "15px" }}>
                      {supported.length === 0 ? (
                        <span>-</span>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {supported.map((off, idx) => (
                            <div key={idx}>
                              <div style={{ textAlign: "justify" }}>
                                {off?.offense || "-"}{" "}
                                <strong>({off?.section || "-"})</strong>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>

                    {/* Column 3 (Evidence aligned to Column 2) */}
                    <td
                      style={{
                        textAlign: "justify",
                        verticalAlign: "top",
                        paddingTop: "15px",
                      }}
                    >
                      {supported.length === 0 ? (
                        <span>-</span>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {supported.map((off, idx) => (
                            <div key={idx}>
                              {renderTwoLineItems(off?.supporting_evidence)}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>


                    {/* Column 4 (Witness aligned to Column 2) */}
                    <td
                      style={{
                        textAlign: "justify",
                        verticalAlign: "top",
                        paddingTop: "15px",
                      }}
                    >
                      {supported.length === 0 ? (
                        <span>-</span>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {supported.map((off, idx) => (
                            <div key={idx}>
                              {renderTwoLineItems(off?.supporting_witnesses)}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </TableSection>
  );
};

const OffensesNotMadeOutTable = ({ offensesSummary = [] }) => {
  return (
    <TableSection>
      <div className="fir-table-container">
        <table className="fir-table">
          <thead>
            <tr>
              <th style={{ width: "22%" }}>Accused Name</th>
              <th style={{ width: "34%" }}>Offenses</th>
              <th>Objection</th>
            </tr>
          </thead>

          <tbody>
            {(!offensesSummary || offensesSummary.length === 0) ? (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", padding: "12px" }}>
                  No offenses summary available.
                </td>
              </tr>
            ) : (
              offensesSummary.map((row, rowIdx) => {
                const unsupported = Array.isArray(row?.unsupported_sections)
                  ? row.unsupported_sections
                  : [];

                return (
                  <tr key={`${row?.accused_name || "accused"}-${rowIdx}`}>
                    {/* Column 1 */}
                    <td style={{ verticalAlign: "top", paddingTop: "15px" }}>
                      {row?.accused_name || "-"}
                    </td>

                    {/* Column 2 */}
                    <td style={{ verticalAlign: "top", paddingTop: "15px" }}>
                      {unsupported.length === 0 ? (
                        <span>-</span>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {unsupported.map((u, idx) => (
                            <div key={idx}>
                              <div style={{ textAlign: "justify" }}>
                                {u?.section || "-"}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>

                    {/* Column 3 */}
                    <td
                      style={{
                        textAlign: "justify",
                        verticalAlign: "top",
                        paddingTop: "15px",
                      }}
                    >
                      {unsupported.length === 0 ? (
                        <span>-</span>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {unsupported.map((u, idx) => (
                            <div key={idx}>
                              {u?.why_unsupported || "-"}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </TableSection>
  );
};

const ProbableLapsesTable = ({ lapsesAudit }) => {
  const lapses = Array.isArray(lapsesAudit?.investigation_lapses)
    ? lapsesAudit.investigation_lapses
    : [];

  const formatCategoryLabel = (s) => {
  if (!s) return "-";
  return String(s)
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

  return (
    <TableSection>
      <div className="fir-table-container">
        <table className="fir-table">
          <thead>
            <tr>
              <th style={{ width: "22%" }}>Category</th>
              <th>Description</th>
            </tr>
          </thead>

          <tbody>
            {lapses.length === 0 ? (
              <tr>
                <td colSpan={2} style={{ textAlign: "center", padding: "12px" }}>
                  No probable lapses found.
                </td>
              </tr>
            ) : (
              lapses.map((lapse, idx) => (
                <tr key={`${lapse?.category || "lapse"}-${idx}`}>
                  <td style={{ verticalAlign: "top", paddingTop: "15px" }}>
                    {formatCategoryLabel(lapse?.category)}
                  </td>
                  <td
                    style={{
                      textAlign: "justify",
                      verticalAlign: "top",
                      paddingTop: "15px",
                    }}
                  >
                    {lapse?.description || "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </TableSection>
  );
};

/* ======================= Main Page ======================= */
const VerifiedChargeSheet = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [fir] = useState(location.state?.fir ?? null);
  const [showChargeSheet, setShowChargeSheet] = useState(false);

  const formatDateTime = (isoString) => {
    if (!isoString) return "";

    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";

    const pad = (n) => String(n).padStart(2, "0");

    // Keeping the same UTC formatting style used in your GeneratedCS.js page
    // (If uploadedOn is already local time, and you want local formatting, switch to getDate/getHours etc.)
    const day = pad(date.getUTCDate());
    const month = pad(date.getUTCMonth() + 1);
    const year = date.getUTCFullYear();
    const hours = pad(date.getUTCHours());
    const minutes = pad(date.getUTCMinutes());

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const handlePrint = () => {
    // Same print styling pattern as GeneratedCS.js
    const style = document.createElement("style");
    style.innerHTML = `
      @page {
        margin-top: 5cm;
        margin-bottom: 1cm;
        margin-left: 1cm;
        margin-right: 1cm;
        scale: 0.9;
      }`;
    document.head.appendChild(style);
    window.print();
    document.head.removeChild(style);
  };

  const uploadedOnText = formatDateTime(fir?.chargeSheet?.uploadedOn);

  return (
    <div className="fir-page-container">
      <Navbar />
      <main style={{ flex: 1 }}>
        <br />
        <br />

        {/* Heading */}
        <div className="info-section" style={{ marginTop: "35px" }}>
          <center>
            <b>Charge Sheet Audit Report</b>
            <br />
            <span style={{ fontSize: "14px", fontWeight: "600" }}>
              (Generated from Charge Sheet uploaded on {uploadedOnText || "-"} Hrs)
            </span>
            <br />
            <span style={{ fontSize: "16px", fontWeight: "bold" }}>
              AI-generated. Verify the information before taking any action.
            </span>
          </center>
        </div>

        {/* View Charge Sheet */}
        <div className="info-section">
          <center>
            <button
              className="submit-button"
              style={{
                marginLeft: "auto",
                backgroundColor: "#FFFFFF",
                color: "#094268",
                fontWeight: "bold",
                border: "1px solid #094268",
              }}
              onClick={() => setShowChargeSheet(true)}
            >
              View Charge Sheet
            </button>
          </center>
        </div>

        <div className="fir-page">
          {/* Single table: Discrepancies */}
          <div className="info-section">
            <p>
              <b>Discrepancies</b>
            </p>
          </div>
          {fir?.chargeSheet?.discrepancyAudit?.notes && (
            <p>
              <b>Notes:</b> {fir.chargeSheet.discrepancyAudit.notes}
            </p>
          )}
          <DiscrepanciesTable discrepancies={fir?.chargeSheet?.discrepancyAudit?.discrepancies} />

          <div className="info-section">
            <p>
              <b>Offenses Made Out</b>
            </p>
          </div>
          <OffensesMadeOutTable offensesSummary={fir?.chargeSheet?.offensesSummary} />

          <div className="info-section">
            <p>
              <b>Offenses Not Made Out</b>
            </p>
          </div>
          <OffensesNotMadeOutTable offensesSummary={fir?.chargeSheet?.offensesSummary} />

          {/* <div className="info-section">
            <p>
              <b>Probable Lapses</b>
            </p>
          </div>
          <ProbableLapsesTable lapsesAudit={fir?.chargeSheet?.lapsesAudit} /> */}

          {/* Bottom: Back link + right side buttons */}
          <div
            className="info-section"
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "-30px",
              marginBottom: "50px",
              padding: "0 20px",
              width: "100%",
            }}
          >
            {/* Left: Back */}
            <div className="info-section">
              <Link
                to="/fir/chargesheet"
                state={{ fir: fir }}
                style={{
                  color: "#007AFF",
                  textDecoration: "none",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "left",
                  gap: "6px",
                  fontSize: "16px",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: "24px",
                    fontWeight: 800,
                    lineHeight: "20px",
                    marginRight: "2px",
                  }}
                >
                  ←
                </span>
                <span>Back</span>
              </Link>
            </div>

            {/* Right: Reverify + Print */}
            <div style={{ display: "flex", alignItems: "center" }}>
              <button
                onClick={() => navigate("/fir/chargesheet/upload", { state: { fir: fir } })}
                className="print-button"
                style={{
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  padding: "8px 14px",
                  cursor: "pointer",
                  marginRight: "20px",
                }}
              >
                Reverify Charge Sheet
              </button>

              <button
                onClick={handlePrint}
                className="print-button"
                style={{
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  padding: "8px 14px",
                  cursor: "pointer",
                  marginRight: "20px",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="20"
                  viewBox="0 0 24 24"
                  width="20"
                  fill="currentColor"
                  style={{ marginRight: "5px" }}
                >
                  <path d="M0 0h24v24H0z" fill="none" />
                  <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 
                    1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"
                  />
                </svg>
                Print
              </button>
            </div>
          </div>
        </div>

        {/* Popup: View Charge Sheet */}
        {showChargeSheet && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Charge Sheet</h3>

              <div
                style={{
                  fontSize: "16px",
                  textAlign: "justify",
                  maxHeight: "50vh",
                  overflowY: "auto",
                  marginBottom: "16px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {fir?.chargeSheet?.text
                  ? fir.chargeSheet.text.split("\n").map((line, index) => (
                    <span key={index}>
                      {line}
                      <br />
                    </span>
                  ))
                  : "-"}
              </div>

              <div className="popup-buttons">
                <button className="submit-popup-button" onClick={() => setShowChargeSheet(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default VerifiedChargeSheet;